"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Comment, CreateCommentInput } from "@theta/api-client";
import { createComment } from "@/lib/api/posts";
import { optimisticAttachments, optimisticPublishedAt } from "@/lib/attachments";
import type { Attachment as TrayAttachment } from "@/lib/postRaw";
import {
  dropLocalComment,
  placeInThread,
  rememberLocalComment,
} from "@/lib/thread";
import { commentsKey } from "./useComments";
import { useCurrentUser } from "./useCurrentUser";

/**
 * WRITING A COMMENT, ONCE — the card's box and every reply composer
 *
 * THE RESPONSE IS THE COMMENT, SO IT IS SPLICED IN, NOT ASKED FOR AGAIN. This
 * used to be `invalidateQueries(commentsKey(postId))` in two places, which is
 * where OSO-563 lived: in the feed NOTHING observes that key (`useComments`
 * only mounted on the post page), the reply-thread key it also invalidated
 * belonged to a DISABLED query, and the query that actually rendered the
 * comments — the feed's own page — was never told at all. The reply reached
 * the database on the first try and then waited for a poll that stops once you
 * have scrolled past page one. A write-through does not care who is observing
 * or whether anything is polling.
 *
 * THE OPTIMISTIC ROW IS HERE FOR THE SAME REASON IT IS ONE PLACE: the card's
 * composer had one and the reply composer did not, so a comment felt instant
 * and a reply felt broken. Both now go through `onMutate`.
 *
 * `postedTo` is the URL's post (C4) — the post's own id for a top-level
 * comment, a comment's id for a reply — and `rootPostId` is the thread it all
 * hangs off. They are the same number for a top-level comment, and `PostCard`
 * passes exactly that.
 */

export interface NewComment {
  bodyMarkdown: string;
  attachments: CreateCommentInput["attachments"];
  /**
   * The tray's own rows, captured at `mutate()` for the same reason the ids
   * are (OSO-539): the composer empties the tray on the next line, so the
   * placeholder cannot read them off the hook afterwards.
   */
  trayAttachments?: TrayAttachment[];
  isAnonymous?: boolean;
  /** The link whose preview card the author closed in the composer (OSO-568). */
  dismissedLinkPreviewUrl?: string | null;
}

interface Context {
  tempId: number | null;
}

export function useCreateComment({
  rootPostId,
  postedTo,
}: {
  rootPostId: number;
  postedTo: number;
}) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const key = commentsKey(rootPostId);

  /**
   * `old ?? []` rather than a no-op on `undefined`: the post page has no seed,
   * so between mount and the first thread read this entry does not exist —
   * and dropping the write there threw away BOTH the placeholder and the
   * server's own row, leaving the comment to turn up on some later poll. The
   * in-flight read replaces this one-row list with the whole thread when it
   * lands, with the row still in it.
   */
  const patch = (edit: (thread: Comment[]) => Comment[]) => {
    queryClient.setQueryData<Comment[]>(key, (old) => edit(old ?? []));
  };

  return useMutation<Comment, Error, NewComment, Context>({
    mutationFn: (vars) =>
      createComment(postedTo, {
        bodyMarkdown: vars.bodyMarkdown,
        // ROWS, in display order (§1.12) — no `![name](upload://…)` tokens
        // appended to the body and parsed back out on the other side.
        attachments: vars.attachments,
        ...(vars.isAnonymous ? { isAnonymous: true } : {}),
        ...(vars.dismissedLinkPreviewUrl
          ? { dismissedLinkPreviewUrl: vars.dismissedLinkPreviewUrl }
          : {}),
      }),

    onMutate: async (vars) => {
      // A poll landing between here and `onSuccess` would overwrite the
      // placeholder with a thread that does not have it yet.
      await queryClient.cancelQueries({ queryKey: key });
      if (!currentUser) return { tempId: null };

      const tempId = -Date.now();
      rememberLocalComment(rootPostId, tempId);
      patch((thread) =>
        placeInThread(
          thread,
          optimisticComment(tempId, vars, currentUser),
          postedTo,
          rootPostId,
        ),
      );
      return { tempId };
    },

    onSuccess: (created, _vars, context) => {
      if (context?.tempId != null) {
        dropLocalComment(rootPostId, context.tempId);
        patch((thread) => thread.filter((row) => row.id !== context.tempId));
      }
      // The row the server assembled, not the one we drew: its real id, its
      // rendered body, its attachments and its `can` block.
      rememberLocalComment(rootPostId, created.id as number);
      patch((thread) => placeInThread(thread, created, postedTo, rootPostId));
    },

    onError: (_error, _vars, context) => {
      if (context?.tempId == null) return;
      dropLocalComment(rootPostId, context.tempId);
      patch((thread) => thread.filter((row) => row.id !== context.tempId));
    },
  });
}

/**
 * The placeholder drawn while the request is in flight.
 *
 * `can` is all false and stays that way. The id here is a negative placeholder,
 * so every affordance would address a route that cannot answer it — and
 * guessing a capability client-side is the thing D11 exists to stop. The real
 * row replaces this one and carries the server's own block.
 */
function optimisticComment(
  tempId: number,
  vars: NewComment,
  currentUser: NonNullable<ReturnType<typeof useCurrentUser>["data"]>,
): Comment {
  const anonymous = vars.isAnonymous === true;
  const now = new Date().toISOString();
  return {
    id: tempId as Comment["id"],
    parentId: null,
    depth: 0,
    /** A comment that is being written is not a tombstone (OSO-583). */
    state: "comment",
    author: anonymous
      ? null
      : {
          id: currentUser.id as NonNullable<Comment["author"]>["id"],
          name: currentUser.name,
          avatar: {
            small: currentUser.avatar_template?.replace("{size}", "24") ?? "",
            medium: currentUser.avatar_template?.replace("{size}", "48") ?? "",
            large: currentUser.avatar_template?.replace("{size}", "96") ?? "",
          },
          // The optimistic row for the browser's own logged-in user — never a
          // bot, which has no composer session. Moved here with the rest of
          // the row when this was lifted out of `PostCard`.
          isBot: false,
        },
    authorAffiliation: null,
    isAnonymous: anonymous,
    anonymousAuthorRef: null,
    // Escaped, not rendered: this is the text the user typed, and the server's
    // own HTML replaces it a round trip later.
    bodyHtml: `<p>${vars.bodyMarkdown.replace(/</g, "&lt;")}</p>`,
    // OSO-568. The placeholder body is escaped text with no anchors in it, so
    // nothing derives a card from it either way — but it is carried so that the
    // moment the server's row replaces this one, nothing flickers.
    dismissedLinkPreviewUrl: vars.dismissedLinkPreviewUrl ?? null,
    // The picture is drawn from the tray's own rows until the server's arrive
    // (OSO-543); before that the comment sat without it for the round trip.
    attachments: optimisticAttachments(vars.trayAttachments ?? []),
    createdAt: now,
    /**
     * PUBLISHED, UNLESS THE TRAY SAYS OTHERWISE (OSO-490 §4). `null` means
     * "still encoding, and only its author can see it", which renders the row
     * muted, with no reactions and a line about the video — the wrong guess
     * for the ordinary comment this placeholder mostly is.
     *
     * But the tray KNOWS which case this is, so it is asked rather than
     * guessed either way: `optimisticPublishedAt` is `now` for every comment
     * without a clip still encoding, and `null` for the one that has. Letting
     * the server correct it a round trip later would mean drawing that comment
     * undimmed and interactive and then flipping it — the same picture
     * changing under the reader that `optimisticAttachments` carrying the real
     * status exists to prevent.
     */
    publishedAt: optimisticPublishedAt(vars.trayAttachments ?? []),
    editedAt: null,
    isHidden: false,
    reactions: { counts: [], mine: null },
    replyCount: 0,
    can: {
      edit: false,
      delete: false,
      hide: false,
      unhide: false,
      react: false,
      reply: false,
      flag: false,
    },
  };
}
